import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@/common/styles/style";
import { styles } from "./style";
import { tableHeader } from "./data";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import useCheckbox from "@/hooks/useCheckbox";
import SearchBox from "@/molecules/searchBox";
import { useEffect, useState } from "react";
import DeleteConfirmModal from "@/organisms/deleteConfirmModal";
import { apiMachineType } from "@/common/type/type";
import { Routes } from "@/router/routes";
import CircularProgress from "@/molecules/circularProgress";
import { useLoading } from "@/hooks/useLoading";
import { useAuth } from "@/hooks/useAuth";
import { getRoleName, roleInfo } from "@/helpers/getUserInfo";
import ErrorMessage from "@/atoms/errorMessage";

export default function MachineList() {
  const navigate = useNavigate();
  const [apiMachines, setApiMachines] = useState<apiMachineType[]>([]);
  const [filteredMachines, setFilteredMachines] = useState<apiMachineType[]>(
    []
  );
  const [open, setOpen] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState<apiMachineType | null>(
    null
  );
  const { checkedItems, handleCheckboxChange, handleParentCheckboxChange } =
    useCheckbox(filteredMachines.length);
  const [deleteMessage, setDeleteMessage] = useState<string>("");
  const { loading, startLoading, stopLoading } = useLoading();
  const [error, setError] = useState<string | null>(null);
  const { userRole } = useAuth();

  useEffect(() => {
    const checkServerConnection = async () => {
      startLoading();
      try {
        const response = await fetch("/api/machine/", {
          method: "GET",
          credentials: "include",
        });

        const data = await response.json();
        if (response.ok) {
          console.log(data.message);
          setApiMachines(data.result);
        } else if (response.status === 404) {
          alert(data.message);
          navigate(Routes.ERROR);
        } else if (response.status === 401 || response.status === 403) {
          alert(data.message);
          navigate(Routes.LOGIN);
        } else {
          setError(data.message);
        }
      } catch (error) {
        console.error("サーバ接続エラー:", error);
      } finally {
        stopLoading();
      }
    };
    checkServerConnection();
  }, []);

  useEffect(() => {
    if (apiMachines.length > 0) {
      setFilteredMachines(apiMachines);
    }
  }, [apiMachines]);

  const handleSearch = (value: string) => {
    const searchValue = value.toLowerCase();

    const filtered = apiMachines.filter((machine) => {
      return (
        machine.tenantName.toLowerCase().includes(searchValue) ||
        (machine.groupNames &&
          machine.groupNames.some((group) =>
            group?.toLowerCase().includes(searchValue)
          )) ||
        machine.machineNumber.toLowerCase().includes(searchValue)
      );
    });

    setFilteredMachines(filtered);
  };

  const handleDashboard = async () => {
    if (!checkedItems.some((item) => item)) {
      alert("少なくとも1つの機械を選択してください。");
      return;
    }

    const selectedMachines = filteredMachines.filter(
      (_, index) => checkedItems[index]
    );

    navigate(Routes.DASHBOARD, {
      state: {
        machineIds: (selectedMachines as apiMachineType[]).map(
          (machine) => machine.id
        ),
        selectedMachines: selectedMachines as apiMachineType[],
      },
    });
  };

  const handleRegister = () => {
    navigate(Routes.MACHINE_REGISTER);
  };

  const handleSetting = (machine: apiMachineType) => {
    navigate(`/MachineList/CollectionDataSet/${machine.id}`, {
      state: { selectedMachine: machine },
    });
  };

  const handleModify = (machine: apiMachineType) => {
    navigate(`/MachineList/MachineModify/${machine.id}`, {
      state: { machine: machine },
    });
  };

  const handleDelete = async () => {
    if (!selectedMachine) return;

    try {
      const response = await fetch(`/api/machine/${selectedMachine.id}`, {
        method: "DELETE",
      });

      const data = await response.json();
      if (response.ok) {
        // 取得データを更新
        const updatedMachines = apiMachines.filter(
          (m) => m.id !== selectedMachine.id
        );
        setApiMachines(updatedMachines);
        console.log(data.message);
        // 一覧に反映
        setFilteredMachines(updatedMachines as apiMachineType[]);
        setOpen(false);
        setSelectedMachine(null);
      } else if (response.status === 404) {
        alert(data.message);
      } else {
        console.error("削除失敗: ", await response.text());
        alert(data.message);
      }
    } catch (error) {
      console.error("API呼び出しエラー:", error);
      alert("マシン削除中にエラーが発生しました。");
    }
  };

  const handleOpenModal = (machine: apiMachineType) => {
    setDeleteMessage(machine.machineNumber);
    setSelectedMachine(machine);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedMachine(null);
  };

  return (
    <Box sx={styles.container}>
      <Box>
        <Stack direction="row" sx={styles.titleArea1}>
          <Typography variant="h3" sx={styles.title}>
            機械一覧
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} sx={styles.titleArea2}>
          <Button variant="contained" onClick={handleRegister}>
            <Typography variant="button">機械の登録</Typography>
          </Button>
        </Stack>
        <SearchBox onSearch={handleSearch} />
      </Box>
      <ErrorMessage error={error} />
      {loading ? (
        <CircularProgress />
      ) : (
        <Table>
          <TableHead sx={styles.tableHead}>
            <TableRow>
              <TableCell align="center">
                <Checkbox
                  checked={
                    checkedItems.length > 0 &&
                    checkedItems.every((item) => item)
                  }
                  indeterminate={
                    checkedItems.some((item) => item) &&
                    !checkedItems.every((item) => item)
                  }
                  onChange={handleParentCheckboxChange}
                />
              </TableCell>
              {tableHeader.map((header, index) => (
                <TableCell key={index} align="center">
                  <Typography variant="h6">{header}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMachines.map((machine, index) => (
              <TableRow key={machine.id}>
                <TableCell align="center">
                  <Checkbox
                    checked={checkedItems[index] || false}
                    onChange={(e) => handleCheckboxChange(index, e)}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body1">{machine.tenantName}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    {(Array.isArray(machine.groupNames)
                      ? machine.groupNames
                      : [machine.groupNames]
                    )
                      .flatMap((group) =>
                        typeof group === "string"
                          ? group.split(",").map((name) => name.trim())
                          : []
                      )
                      .map((group, index) => (
                        <Chip label={group} key={index} />
                      ))}
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body1">
                    {machine.machineNumber}
                  </Typography>
                </TableCell>
                {getRoleName(userRole) === roleInfo.admin ? (
                  <TableCell align="center" width="15%">
                    <IconButton onClick={() => handleSetting(machine)}>
                      <SettingsIcon />
                    </IconButton>
                    <IconButton onClick={() => handleModify(machine)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleOpenModal(machine)}>
                      <DeleteIcon />
                    </IconButton>
                    <DeleteConfirmModal
                      open={open}
                      onClose={handleCloseModal}
                      handleDelete={handleDelete}
                      deleteMessage={deleteMessage}
                      backdrop={{ invisible: true }}
                    />
                  </TableCell>
                ) : (
                  <TableCell></TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Stack direction="row" spacing={2} sx={styles.dashboardViewBottanArea}>
        <Button variant="contained" onClick={handleDashboard}>
          <Typography variant="button">ダッシュボード表示</Typography>
        </Button>
      </Stack>
    </Box>
  );
}
